import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import { useGetCricketDetailQuery } from '../services/sports/sportsApi';
import Loading from "../components/Loading";
import moment from "moment";
import {
  SUSPENDED_BG_COLOR,
  SUSPENDED_TEXT_COLOR,
} from "../config/statusStyles";

const ODDS_API_URL = import.meta.env.VITE_ODDS_API_URL || "https://oddsapi.247idhub.com";

// Calendar Icon
const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

// Swap Right Icon
const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

// Menu Down Icon
const MenuDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

// Fetch active matches
const fetchActiveMatches = async function () {
  try {
    const token = getAccessToken();

    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.get(
      ODDS_API_URL + "/betfair_api/active_match/v5/4",
      {
        headers: { Authorization: "Bearer " + token },
      },
    );

    const data = response.data;

    if (data.status === false) {
      return {
        success: false,
        error: data.message || "Failed to fetch matches",
      };
    }

    return { success: true, data: data.data || [] };
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || "Failed to fetch matches";
    return { success: false, error: errorMessage };
  }
};

// Dropdown Menu Component
const DropdownMenu = ({ match, position, onClose }) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const rawMatchName = match.matchName;
  const matchName =
    rawMatchName && rawMatchName !== "undefined" && rawMatchName !== "null"
      ? rawMatchName
      : match.marketName || match.eventName || match.name || String(match.eventId || match.matchId || "Unknown");

  useEffect(
    function () {
      const handleClickOutside = function (event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return function () {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    },
    [onClose],
  );

  const handleMatchPosition = function (e) {
    e.preventDefault();
    navigate("/dashboard/match/" + (match.eventId || match.matchId));
  };

  const menuItems = [
    {
      key: "position",
      label: "Match and Session Position",
      onClick: handleMatchPosition,
    },
    {
      key: "plusminus",
      label: "Match and Session Plus Minus",
      onClick: function () {
        navigate(
          "/sports-betting/match-session-plus-minus/" +
            (match.eventId || match.matchId) +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "matchbets",
      label: "Display Match Bets",
      onClick: function () {
        navigate(
          "/sports-betting/match-bets/" +
            (match.eventId || match.matchId) +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "tossbets",
      label: "Display Toss Bets",
      onClick: function () {
        navigate(
          "/sports-betting/toss-bets/" +
            (match.eventId || match.matchId) +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "sessionbets",
      label: "Display Session Bets",
      onClick: function () {
        const id = match.eventId || match.matchId;
        const path = "/sports-betting/fancy-bets/" + id + "/" + encodeURIComponent(matchName);
        console.log("Navigating to Session Bets:", path, "ID:", id, "Name:", matchName);
        navigate(path);
        onClose();
      },
    },
    {
      key: "completed",
      label: "Completed Fancies",
      onClick: function () {
        const id = match.eventId || match.matchId;
        const name = match.marketName || match.matchName || match.eventName || match.name || "Unknown";
        console.log("Navigating to completed fancies:", id, name);
        navigate(
          "/sports-betting/fancy-profit-loss/" +
            id +
            "/" +
            encodeURIComponent(name),
        );
        onClose();
      },
    },
    {
      key: "rejected",
      label: "Rejected Bets",
      onClick: function () {
        const id = match.eventId || match.matchId;
        const name = match.marketName || match.matchName || match.eventName || match.name || "Unknown";
        console.log("Navigating to rejected bets:", id, name);
        navigate(
          "/sports-betting/rejected-bets/" +
            id +
            "/" +
            encodeURIComponent(name),
        );
        onClose();
      },
    },
    {
      key: "agent",
      label: "Agent Plus Minus",
      onClick: function() {
        navigate('/sports-betting/agent-plus-minus/' + (match.eventId || match.matchId) + '/' + encodeURIComponent(matchName));
        onClose();
      }
    },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      <ul
        ref={menuRef}
        className="absolute bg-white rounded shadow-lg z-50 py-1 min-w-[230px] list-none m-0 p-0"
        style={{
          top: position.top,
          left: position.left,
          boxShadow:
            "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
        }}
        role="menu"
      >
        {menuItems.map(function (item) {
          return (
            <li
              key={item.key}
              role="menuitem"
              tabIndex={-1}
              onClick={
                item.onClick ||
                function () {
                  console.log("Navigate to:", item.path);
                  onClose();
                }
              }
              className="px-4 py-1 text-sm cursor-pointer transition-colors hover:bg-gray-100 text-gray-800"
              style={{ lineHeight: "22px", fontWeight: 400 }}
            >
              <span>{item.label}</span>
            </li>
          );
        })}
      </ul>
    </>
  );
};

// Format date for display
const formatDisplayDate = function (dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");

  return (
    day +
    "-" +
    month +
    "-" +
    year +
    " " +
    hoursStr +
    ":" +
    minutes +
    ":" +
    seconds +
    " " +
    ampm
  );
};

const ActiveGamesPage = () => {
  const navigate = useNavigate();

  // Helper function to convert date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Helper function to convert date from DD/MM/YYYY to YYYY-MM-DD
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  // Date states for API - Default to last 10 days
  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);
  
  const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const tenDaysAgoFormatted = `${tenDaysAgo.getFullYear()}-${String(tenDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(tenDaysAgo.getDate()).padStart(2, '0')}`;
  
  const [fromDate, setFromDate] = useState(tenDaysAgoFormatted);
  const [toDate, setToDate] = useState(todayFormatted);

  // Call cricket detail API with formatted dates
  const { data: cricketData, isLoading: cricketLoading, error: cricketError } = useGetCricketDetailQuery({
    from: formatDateForAPI(fromDate),
    to: formatDateForAPI(toDate)
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  // Update matches when cricket data is available
  useEffect(() => {
    if (cricketData && cricketData.data) {
      setMatches(cricketData.data);
      setLoading(false);
    } else if (cricketError) {
      setError(cricketError.message || "Failed to load cricket details");
      setLoading(false);
    } else if (cricketLoading) {
      setLoading(true);
    }
  }, [cricketData, cricketError, cricketLoading]);

  const handleDropdownClick = function (e, matchId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setActiveDropdown(activeDropdown === matchId ? null : matchId);
  };

  const handleCloseDropdown = function () {
    setActiveDropdown(null);
  };

  const handleStatusClick = function(match) {
    setSelectedMatch(match);
    setShowConfirmModal(true);
  };

  const handleConfirmChange = function() {
    // Here you can add API call to update the status
    console.log('Status change confirmed for:', selectedMatch);
    setShowConfirmModal(false);
    setSelectedMatch(null);
  };

  const handleCancelChange = function() {
    setShowConfirmModal(false);
    setSelectedMatch(null);
  };

  const activeDropdownMatch = matches.find(function (m) {
    return (m.matchId === activeDropdown || m.eventId === activeDropdown);
  });

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 py-2 flex items-center justify-between">
              <span className="text-xl text-white font-normal capitalize">
                Sport Details
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Date Range Picker */}
            <div className="flex justify-center py-2">
              <div
                className="flex items-center border border-gray-300 bg-white"
                style={{ width: "320px", borderRadius: 0 }}
              >
                <div className="flex-1 px-2 py-2">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    placeholder="Start date"
                    className="w-full text-sm focus:outline-none bg-transparent"
                    style={{ fontSize: "12px" }}
                  />
                </div>
                <div className="px-2 text-gray-400">
                  <SwapRightIcon />
                </div>
                <div className="flex-1 px-2 py-2">
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    placeholder="End date"
                    className="w-full text-sm focus:outline-none bg-transparent"
                    style={{ fontSize: "12px" }}
                  />
                </div>
                <div className="px-2 text-gray-400">
                  {/* <CalendarIcon /> */}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-[#0F2327] text-white">
                      <th className="p-2 border border-gray-600 text-left font-medium"></th>
                      <th className="p-2 border border-gray-600 text-left font-medium">Event Id</th>
                      <th className="p-2 border border-gray-600 text-left font-medium">Market Name</th>
                      <th className="p-2 border border-gray-600 text-left font-medium">Time</th>
                      <th className="p-2 border border-gray-600 text-left font-medium">Won by</th>
                      <th className="p-2 border border-gray-600 text-left font-medium">Profit Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          No active games found
                        </td>
                      </tr>
                    ) : (
                      matches.map(function (match, index) {
                        const rowBgClass = index % 2 === 0 ? "" : "bg-gray-50";
                        const matchName = match.marketName || match.matchName || match.eventName || match.name || "-";
                        const statusValue =
                          match?.settings ||
                          match?.gstatus ||
                          match?.status ||
                          match?.marketStatus ||
                          match?.state ||
                          "";
                        const isSuspended =
                          match?.suspended === true ||
                          String(statusValue).toLowerCase() === "suspended";

                        return (
                          <tr
                            key={match.eventId || match.matchId}
                            className={rowBgClass + " hover:bg-gray-100"}
                          >
                            <td className="p-2 border border-gray-300">
                              <span
                                onClick={function (e) {
                                  handleDropdownClick(e, match.eventId || match.matchId);
                                }}
                                className="bg-blue-500 text-white cursor-pointer inline-flex items-center justify-center"
                                style={{ padding: "3px 3px" }}
                              >
                                <MenuDownIcon />
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.eventId || match.matchId || "-"}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {matchName}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.startTime ? moment.utc(match.startTime).format('DD/MM/YYYY hh:mm A') : '-'}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.wonBy || "-"}
                            </td>
                            <td className={`p-2 border border-gray-300 ${match.profitLoss >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                              {match.profitLoss !== undefined ? match.profitLoss : '-'}
                           
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {activeDropdown && activeDropdownMatch && (
        <DropdownMenu
          match={activeDropdownMatch}
          position={dropdownPosition}
          onClose={handleCloseDropdown}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-[#2c3e3e] text-white px-5 py-3 rounded-t-lg">
              <h3 className="text-lg font-normal">Confirm Changes</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to change the status for:
              </p>
              <p className="font-semibold text-gray-900 mb-4">
                {selectedMatch?.marketName || selectedMatch?.matchName || 'this match'}?
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCancelChange}
                  className="px-6 py-2 bg-gray-500 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmChange}
                  className="px-6 py-2 bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveGamesPage;
